import { LifeErrorFallback } from '@festina-life/components'
import { FullscreenSpinner } from '@festina-life/ui/feedback'
import { type PropsWithChildren, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useLocation } from 'react-router'

import { ChangesetStateProvider } from '../context/ChangesetStateProvider'
import { DraftProvider } from '../context/DraftProvider'
import { FPlusFormattedExpressionsProvider } from '../context/FPlusFormattedExpressionsProvider'
import { FPlusProvider } from '../context/FPlusProvider'
import { FPlusTypeCheckingContextProvider } from '../context/FPlusTypeCheckingContextProvider'
import { LayoutProvider } from '../context/LayoutProvider'
import { MetaModelProvider } from '../context/MetaModelProvider'
import { ParameterProvider } from '../context/ParameterProvider'
import { TemplateProvider } from '../context/TemplateProvider'
import { ApplicationViewOperationsProvider } from '../workspace/ApplicationViewOperationsProvider'
import { OperationsProvider } from '../workspace/OperationsProvider'
import { WorkspaceManagerProvider } from '../workspace/WorkspaceManagerProvider'
import { PersonaRolesGuard } from './PersonaRolesGuard'
import { useNavigationParams } from './useNavigationParams'

export const ConfigurationCommonProviders = ({
  children,
}: PropsWithChildren) => {
  const location = useLocation()
  const { branch, server } = useNavigationParams()

  return (
    <ErrorBoundary
      resetKeys={[location]}
      FallbackComponent={(props) => (
        <LifeErrorFallback {...props} className="m-3" />
      )}
    >
      <Suspense fallback={<FullscreenSpinner />}>
        <PersonaRolesGuard>
          <LayoutProvider>
            <MetaModelProvider>
              <FPlusProvider>
                <WorkspaceManagerProvider>
                  {(currentWorkspaceKey) => (
                    <ChangesetStateProvider>
                      <OperationsProvider
                        workspaceKey={currentWorkspaceKey}
                        branchKey={
                          branch ? { server, name: branch } : undefined
                        }
                        isDisabledChangesetsRelevant
                      >
                        <ApplicationViewOperationsProvider>
                          <DraftProvider>
                            <TemplateProvider>
                              <ParameterProvider>
                                <FPlusTypeCheckingContextProvider>
                                  <FPlusFormattedExpressionsProvider>
                                    {children}
                                  </FPlusFormattedExpressionsProvider>
                                </FPlusTypeCheckingContextProvider>
                              </ParameterProvider>
                            </TemplateProvider>
                          </DraftProvider>
                        </ApplicationViewOperationsProvider>
                      </OperationsProvider>
                    </ChangesetStateProvider>
                  )}
                </WorkspaceManagerProvider>
              </FPlusProvider>
            </MetaModelProvider>
          </LayoutProvider>
        </PersonaRolesGuard>
      </Suspense>
    </ErrorBoundary>
  )
}
